import React, { useMemo, useState } from "react";

const COLOR = {
  green: {
    label: "Good",
    short: "G",
    bg: "#ecfdf5",
    border: "#86efac",
    text: "#166534",
    dot: "#22c55e",
    soft: "#f0fdf4",
  },
  yellow: {
    label: "Almost",
    short: "Y",
    bg: "#fffbeb",
    border: "#facc15",
    text: "#854d0e",
    dot: "#eab308",
    soft: "#fefce8",
  },
  red: {
    label: "Needs work",
    short: "R",
    bg: "#fef2f2",
    border: "#fca5a5",
    text: "#991b1b",
    dot: "#ef4444",
    soft: "#fff1f2",
  },
};

function colorOf(value) {
  return COLOR[value] || COLOR.green;
}

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function countChanges(reply) {
  let words = 0;
  let letters = 0;

  for (const word of reply?.ratings || []) {
    if (word.rating && word.rating !== "green") words += 1;

    for (const letter of word.letters || []) {
      if (letter.rating && letter.rating !== "green") letters += 1;
    }
  }

  const sentenceChanged =
    reply?.sentenceRating && reply.sentenceRating !== "green" ? 1 : 0;

  return {
    sentenceChanged,
    words,
    letters,
    total: sentenceChanged + words + letters,
  };
}

function getAuthorName(reply) {
  return reply?.username || reply?.user || reply?.author || "User";
}

function getNote(reply) {
  const note = reply?.content || reply?.text || reply?.note || "";

  if (!note || note === "Human pronunciation verdict") return "";

  return String(note).trim();
}

function getChapterDisplay(reply) {
  return reply?.chapterTitle || reply?.file || "";
}

function RatingDot({ rating, size = "md", title }) {
  const meta = colorOf(rating);

  return (
    <span
      title={title || meta.label}
      className={cx(
        "inline-block shrink-0 rounded-full",
        size === "sm" ? "h-2 w-2" : "h-3 w-3"
      )}
      style={{ background: meta.dot }}
    />
  );
}

function WordDotGroup({ ratings }) {
  if (!ratings?.length) return null;

  return (
    <div className="flex max-w-[180px] flex-wrap items-center gap-1.5 sm:max-w-[260px]">
      {ratings.map((word, index) => (
        <RatingDot
          key={`${word?.index ?? index}-${word?.word || "word"}`}
          rating={word?.rating || "green"}
          size="sm"
          title={`${word?.word || "Word"}: ${
            colorOf(word?.rating || "green").label
          }`}
        />
      ))}
    </div>
  );
}

function LetterDots({ letters = [] }) {
  if (!letters.length) return null;

  return (
    <div className="mt-2 flex flex-wrap justify-center gap-1">
      {letters.map((letter, index) => (
        <span
          key={`${letter?.index ?? index}-${letter?.char || ""}`}
          className="inline-flex h-6 min-w-6 items-center justify-center rounded-lg border px-1.5 text-[11px] font-black"
          style={{
            background: colorOf(letter?.rating).bg,
            borderColor: colorOf(letter?.rating).border,
            color: colorOf(letter?.rating).text,
          }}
          title={colorOf(letter?.rating).label}
        >
          {letter?.char}
        </span>
      ))}
    </div>
  );
}

export default function RateMeReplyBlock({ reply }) {
  const [open, setOpen] = useState(false);

  const author = getAuthorName(reply);
  const chapterDisplay = getChapterDisplay(reply);

  const sentenceRating = reply?.sentenceRating || "green";
  const sentenceMeta = colorOf(sentenceRating);
  const ratings = reply?.ratings || [];
  const note = getNote(reply);

  const changes = useMemo(() => countChanges(reply), [reply]);

  const changedText =
    changes.total === 0
      ? "All green"
      : `${changes.total} mark${changes.total === 1 ? "" : "s"}`;

  return (
    <div
      className="mt-3 rounded-[20px] border px-3 py-3 shadow-sm"
      style={{
        background:
          "linear-gradient(135deg, #ffffff 0%, #f8fafc 58%, #f0fdf4 100%)",
        borderColor: sentenceMeta.border,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-start gap-3 text-left"
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
             

              <span
                className="rounded-full border px-2 py-0.5 text-[10px] font-black"
                style={{
                  background: sentenceMeta.bg,
                  borderColor: sentenceMeta.border,
                  color: sentenceMeta.text,
                }}
              >
                {sentenceMeta.label}
              </span>

              <span className="text-xs font-bold text-slate-500">
                {changedText}
              </span>

              {chapterDisplay && (
                <span className="max-w-[220px] truncate rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-black text-slate-500">
                  {chapterDisplay}
                </span>
              )}
            </div>

            <div className="mt-1 flex items-center gap-2">
              <WordDotGroup ratings={ratings} />
            </div>

            {note && (
              <div className="mt-2 line-clamp-2 rounded-xl border border-slate-200 bg-white/75 px-3 py-2 text-xs font-semibold leading-5 text-slate-700">
                “{note}”
              </div>
            )}
          </div>
        </div>

        <span
          className="mt-0.5 shrink-0 rounded-full px-3 py-1.5 text-xs font-black shadow-sm"
          style={{
            background: open ? "#f1f5f9" : "#ffffff",
            color: "#334155",
            border: "1px solid #e2e8f0",
          }}
        >
          {open ? "Hide" : "Details"}
        </span>
      </button>

      {open && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white/80 p-3">
          <div className="flex flex-wrap gap-2">
            {ratings.map((word, index) => {
              const wordMeta = colorOf(word?.rating);

              return (
                <div
                  key={`${word?.index ?? index}-${word?.word || "word"}`}
                  className="rounded-2xl border px-3 py-2 shadow-sm"
                  style={{
                    background: wordMeta.bg,
                    borderColor: wordMeta.border,
                  }}
                >
                  <div
                    className="text-center text-xs font-black"
                    style={{ color: wordMeta.text }}
                  >
                    {word?.word}
                  </div>

                  <LetterDots letters={word?.letters || []} />
                </div>
              );
            })}
          </div>

          {note && (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Note
              </div>

              <p className="mt-1 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-800">
                {note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}